import React, { useEffect, useMemo, useState } from "react";
import PageTitle from "../../components/PageTitle/PageTitle";
import Datagrid from "../../components/Datagrid/Datagrid";
import { useSite } from "../../context/siteContext";
import { Alert, Badge, Box, CircularProgress, IconButton, Link, Tab, Tabs, Tooltip } from "@mui/material";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/authContext";
import { useCart } from "../../context/cartContext";
import { ASSETS_BASE_URL, DEFAULT_LAYOUT, RSPL_LIST } from "../../config";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import EmailIcon from "@mui/icons-material/Email";
import RequestsHistory from "../../components/RequestsHistory/RequestsHistory";
import DataGridSkeleton from "../../components/PageSkeletons/DataGridSkeleton";
import { ALERT_MESSAGES, LABELS, TABLE_HEADERS, UI_TEXT } from "../../constants";
import { useTranslation } from "react-i18next";
import { getRequestListBySiteId, deleteFromRequestList, notifyCustomerCare } from "../../services/requestsService";
import { AddShoppingCartSharp } from "@mui/icons-material";
import useBrandingAssets from "../../hooks/useBrandingAssets";
import { useOptions } from "../../context/optionContext";
import { buildRsplCodeToLabelMap, getRsplLabelForCode, parseRsplList } from "../../utilities/rspl";
import { formatMachineNumberFromRow } from "../../utilities/machineDisplay";

const Requests = () => {
	const [availableToPurchaseList, setAvailableToPurchaseList] = useState([]);
	const [requestList, setRequestList] = useState([]);
	const [loading, setloading] = useState(true);
	const [selectedRLRows, setSelectedRLRows] = useState([]);
	const [selectedATCRows, setSelectedATCRows] = useState([]);
	const [buttonLoading, setButtonLoading] = useState(false);
	const [buttonSuccess, setButtonSuccess] = useState(false);
	const [deletingRequestRowIds, setDeletingRequestRowIds] = useState([]);
	const [error, setError] = useState("");
	const { selectedSite, selectedSiteData } = useSite();
	const navigate = useNavigate();
	const { user, hasPermission } = useAuth();
	const [activeTab, setActiveTab] = useState(0);
	const { addMultipleToCart } = useCart();
	const {t} = useTranslation();
	const { stockImageUrl } = useBrandingAssets();
	const { optionKeys } = useOptions();
	const isLayout2 = String(DEFAULT_LAYOUT || "").toLowerCase() === "layout2";
	const requestsTitle = isLayout2
		? t('LABELS.CUSTOMER_CARE_REQUESTS')
		: t('LABELS.REQUEST');
	const resolvedRsplList = useMemo(
		() => parseRsplList(optionKeys?.rsplList, RSPL_LIST),
		[optionKeys?.rsplList]
	);
	const rsplMapping = useMemo(
		() => buildRsplCodeToLabelMap(resolvedRsplList),
		[resolvedRsplList]
	);
	const getPartTypeLabel = (row = {}) =>
		getRsplLabelForCode(row?.product_rspl_code, rsplMapping, t('UI_TEXT.OTHER'));
	const resolveProductRsplCode = (item = {}) => (
		item?.product_rspl_code
		|| item?.productData?.product_rspl_code
		|| item?.productData?.rspl_code
		|| item?.rspl_code
		|| ""
	);

	const canViewHistory = hasPermission("requests_management.view_request_history")
	const canNotifyCCR = hasPermission("requests_management.allow_notify_customer_care")

	const handleTabChange = (event, newValue) => {
		setActiveTab(newValue);
	};

	useEffect(() => {
		const fetchReqList = async () => {
			const req_body = {
				site_number: selectedSite,
				site_id: selectedSiteData?.site_id,
				cus_id: selectedSiteData?.cus_id,
			};

			try {
				const response =  await getRequestListBySiteId(req_body);
				console.log("In requests, getrequestlistbysiteid response", response);
				if (response.status) {
					const products = response.data;
					const purchasableProducts = products
						.filter((product) => product.productData)
						.map((product) => {
							const item = product.productData;
							return {
								product_id: item.product_id,
								product_name: item.product_name,
								product_number: item.product_number,
									product_rspl_code: resolveProductRsplCode(product) || resolveProductRsplCode(item),
									asset_data: item.asset_data,
									machine_id: product.machine_id,
									machine_number: product.machineData?.machine_number,
									line_name: product.machineData?.line_name || product.line_name,
								};
							});

					const nonPurchasableProducts = [];

					setRequestList(nonPurchasableProducts);
					setAvailableToPurchaseList(purchasableProducts);
					setloading(false);
				}
			} catch (error) {
				console.error("Error fetching Request List:", error);
				setError(error);
			}
		};
		if (selectedSite !== "") fetchReqList();
	}, [selectedSite, selectedSiteData?.site_id, selectedSiteData?.cus_id]);

	const handleRowSelect = (rows) => {
		setSelectedRLRows(rows);
	};

	const handleRowSelectPurch = (rows) => {
		setSelectedATCRows(rows);
	};

	const deleteRequestRows = async (rowIds = []) => {
		const ids = Array.isArray(rowIds) ? rowIds : [];
		if (ids.length === 0) {
			setButtonLoading(false);
			Swal.fire({
				icon: "warning",
				title: t('ALERT_MESSAGES.NO_PRODUCTS_SELECTED'),
				text: t('GENERAL.PLEASE_SELECT_PRODUCTS_TO_REMOVE_FROM_REQUEST_LIST'),
			});
			return;
		}
		const selectedRowsData = ids.map((id) => {
			return requestList.find((row) => row.product_id === id);
		}).filter(Boolean);
		try {
			setDeletingRequestRowIds((prev) => Array.from(new Set([...prev, ...ids])));
			const response = await deleteFromRequestList({
				selected_rows: selectedRowsData,
				selectedSite: selectedSite,
				selectedSiteId: selectedSiteData?.site_id,
				selectedCusId: selectedSiteData?.cus_id,
			});
			if (response.status) {
				setRequestList((prevList) => prevList.filter((row) => !ids.includes(row.product_id)));
				// Reset selection
				setSelectedRLRows([]);
				Swal.fire({
					title: t('ALERT_MESSAGES.SUCCESS'),
					text: t('GENERAL.PARTS_REMOVED_FROM_REQUEST_LIST'),
					icon: "success",
				});
			} else {
				Swal.fire({
					icon: "error",
					title: t('EULA_ADMIN.PERMISSION_DENIED'),
					text: response.error || t('EULA_ADMIN.YOU_DO_NOT_HAVE_PERMISSION_TO_PERFORM_THIS_ACTION'),
				});
			}
		} catch (error) {
			console.error("Error deleting from Request List:", error);
			setError(error);
			Swal.fire({
				icon: "error",
				title: "Error",
				text: "An unexpected error occurred. Please try again.",
			});
		} finally {
			setDeletingRequestRowIds((prev) => prev.filter((id) => !ids.includes(id)));
		}
	};
	const handleDeleteSelected = async () => {
		await deleteRequestRows(selectedRLRows);
	};

	const handleNotifyCC = async () => {
		setButtonLoading(true);
		// Check if any rows are selected
		if (selectedRLRows.length === 0) {
			setButtonLoading(false);
			// Show alert if no users are selected
			Swal.fire({
				icon: "warning",
				title: t('ALERT_MESSAGES.NO_PRODUCTS_SELECTED'),
				text: t('ALERT_MESSAGES.PLEASE_SELECT_PRODUCTS_NOTIFY'),
			});
			return;
		}
		const selectedRowsData = selectedRLRows.map((id) => {
			return requestList.find((row) => row.product_id === id);
		});

		console.log(selectedRowsData);

		const req_body = {
			selected_rows: selectedRowsData,
			selectedSite,
			selectedSiteName: selectedSiteData.customer_site_name,
			user_name: user.user_display_name,
			user_email: user.user_email,
			user_phone: user.user_phone,
			shipping_address: selectedSiteData.address_full_ln,
			user_id: user.user_id,
			selectedSiteId: selectedSiteData.site_id,
			selectedCusId: selectedSiteData.cus_id,
		};

		try {
			const response = await notifyCustomerCare(req_body);
			if (response.status) {
				setButtonSuccess(true);
				Swal.fire({
					title: t('ALERT_MESSAGES.SUCCESS'),
					text: t('ALERT_MESSAGES.NOTIFIED_CUSTOMER_CARE'),
					icon: "success",
				});
			}
		} catch (error) {
			console.error("Error fetching Request List:", error);
			setError(error);
		} finally {
			setButtonLoading(false);
		}

		setTimeout(() => {
			setButtonSuccess(false);
		}, 3000);
		// Confirm deletion if users are selected
	};

	const handleAddtoCart = async () => {
		// Check if any rows are selected
		if (selectedATCRows.length === 0) {
			// Show alert if no users are selected
			Swal.fire({
				icon: "warning",
				title: t('ALERT_MESSAGES.NO_PRODUCTS_SELECTED'),
				text: t('ALERT_MESSAGES.PLEASE_SELECE_PRODUCTS_ADD_TO_CART'),
			});
			return;
		}
		const purchasableProducts = [];
		selectedATCRows.forEach((productId) => {
			const product = availableToPurchaseList.find((product) => product.product_id === productId);
			if (product) {
				const productEntry = {
					ProductID: product.product_id,
					Quantity: 1,
					Site: selectedSite,
				};
				purchasableProducts.push(productEntry);
			}
		});
		if (purchasableProducts.length > 0) {
			await addMultipleToCart(purchasableProducts);
			Swal.fire({
				title: t('ALERT_MESSAGES.SUCCESS'),
				text: t('ALERT_MESSAGES.PRODUCTS_ADD_TO_CART'),
				icon: "success",
			});
		}
		// Confirm deletion if users are selected
	};

	var customButtons = [];

	if (canNotifyCCR) {
		customButtons = [
			{
				label: buttonLoading ? <>{t('UI_TEXT.SENDING_EMAIL')}</> : buttonSuccess ? <>{t('UI_TEXT.SENT')}</> : t('UI_TEXT.NOTIFY_CUSTOMER_CARE'),
				onClick: () => {
					handleNotifyCC();
				},
				icon: buttonLoading ? (
					<>
						<CircularProgress size={16} sx={{ color: "white" }} />
					</>
				) : buttonSuccess ? (
					<>
						<CheckCircleIcon sx={{ color: "white" }} />
					</>
				) : (
					<EmailIcon />
				),
			},
			{
				label: t('LABELS.REMOVE_SELECTED'),
				onClick: () => {
					handleDeleteSelected();
				},
				icon: <DeleteIcon />,
			},

		];
	}

	const customButtonsPurch = [
		{
			label: t('LABELS.ADD_TO_CART'),
			icon: <AddShoppingCartSharp />,
			onClick: () => {
				handleAddtoCart();
			},
		},
	];

	const baseColumns = [
		{
			field: "product_name",
			headerName: t('TABLE_HEADERS.PART_NAME'),
			flex: 1,
			renderCell: (params) => {
				const defaultImage = stockImageUrl || `${ASSETS_BASE_URL}/stock.png`;

				const handleError = (e) => {
					e.target.onerror = null; // Prevent infinite loop
					e.target.src = defaultImage; // Fallback to default image
				};

				const src = (() => {
					const raw = params.row?.asset_data?.images?.[0];
					if (raw) {
						if (/^(https?:)?\/\//.test(raw) || raw.startsWith("data:")) return raw;
						const path = raw.startsWith("/") ? raw : `/${raw}`;
						return `${ASSETS_BASE_URL}${path}`;
					}
					return `${ASSETS_BASE_URL}/ProductImages/${params.row.product_number}.png`;
				})();

				return (
					<div style={{ display: "flex", alignItems: "center", gap: "8px", justifyContent: "flex-start" }}>
						<img
							src={src}
							alt={params.row.product_name}
							onError={handleError}
							style={{
								width: "40px",
								height: "40px",
								objectFit: "cover",
								borderRadius: "4px",
							}}
						/>
						<div>
							<Link
								sx={{ cursor: "pointer" }}
								variant="body2"
								onClick={() => {
									navigate(`/machines/${params.row.machine_id}/${params.row.product_id}`);
								}}>
								{params.row.product_name}
							</Link>
						</div>
					</div>
				);
			},
		},
		{ field: "product_number", headerName: t('UI_TEXT.PART'), width: 300 },
		{
			field: "part_type",
			headerName: t('UI_TEXT.PART_TYPE', { defaultValue: 'Part Type' }),
			width: 200,
			renderCell: (params) => getPartTypeLabel(params?.row),
		},
			{
				field: "machine_number",
				headerName: t('TABLE_HEADERS.MACHINE'),
				renderCell: (params) => formatMachineNumberFromRow(params.row),
			},
		];
	const requestColumns = [
		...baseColumns,
		{
			field: "actions",
			headerName: t('TABLE_HEADERS.ACTIONS'),
			width: 110,
			sortable: false,
			align: "center",
			headerAlign: "center",
			renderCell: (params) => (
				<Tooltip title={t('LABELS.REMOVE_SELECTED', { defaultValue: 'Remove' })}>
					<IconButton
						color="error"
						size="small"
						disabled={deletingRequestRowIds.includes(params?.row?.product_id)}
						onClick={(event) => {
							event.stopPropagation();
							if (deletingRequestRowIds.includes(params?.row?.product_id)) return;
							deleteRequestRows([params?.row?.product_id]);
						}}
					>
						{deletingRequestRowIds.includes(params?.row?.product_id) ? (
							<CircularProgress size={16} />
						) : (
							<DeleteIcon fontSize="small" />
						)}
					</IconButton>
				</Tooltip>
			),
		},
	];

	if (loading) {
		return (
			<DataGridSkeleton
				containerClassName="order-history-container"
				showTabs
				tabCount={2}
				actionButtons={2}
				columns={4}
				rows={6}
			/>
		);
	}

	// if (error) {
	//     return (
	//         <Alert severity="error" className="error-alert">
	//             {error}
	//         </Alert>
	//     );
	// }

	return (
		<div className="order-history-container" data-tour="request-buttons">
			<PageTitle title={requestsTitle} />

			{/* {availableToPurchaseList?.length > 0 && (
				<>
					<Alert severity="success" className="error-alert" sx={{ marginTop: "10px" }}>
						Some Products became availabe to purchase - please look into the tab for that
					</Alert>
				</>
			)} */}
			{/* Tabs for switching between Request List and Available to Purchase */}
			<Tabs
				value={activeTab}
				onChange={handleTabChange}
				aria-label="Order History Tabs"
				className="order-history-tabs"
				data-tour="requests-tab">
				<Tab label={t('LABELS.REQUEST_LIST')} />
				<Tab label={<>
					<Box display={"flex"} columnGap={0.75} alignItems={"center"}>
						{t('LABELS.AVAILABLE_TO_PURCHASE')} 
						<span style={{ color: availableToPurchaseList?.length > 0 ? 'green' : "inherit" }}>
							({availableToPurchaseList?.length || 0})
						</span>
					</Box>

				</>} />
				{canViewHistory && <Tab label={t('LABELS.History')} />}
			</Tabs>

			{/* Conditionally render content based on the selected tab */}
			{activeTab === 0 && (
				<>
					{requestList.length === 0 && !loading ? (
						<>
							<Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', rowGap: 2 }}>

						<Alert severity="info" className="error-alert" sx={{ marginTop: "10px" }}>
							{t('ALERT_MESSAGES.NO_PRODUCT_REQUEST_LIST')}
						</Alert>
							</Box>
						</>
					) : (
							<Datagrid
								key="product_id"
								data={requestList}
								columns={requestColumns}
							rowClick={false}
							pageSize={5}
							showSearchBar={true}
							checkboxSelection={true}
							customButtons={customButtons}
							onSelect={handleRowSelect}
								rowIdField="product_id"
								sortBy={[{ field: "product_name", sort: "asc" }]}
								data-tour="request-buttons"
								hideFilterFromColumnMenu
								lockActiveColumnInManageColumns
								preventHidingLastColumn
							/>
					)}
				</>
			)}

			{activeTab === 1 && (
				<>
					{availableToPurchaseList.length === 0 && !loading ? (
						<Alert severity="info" className="error-alert" sx={{ marginTop: "10px" }}>
							{t('ALERT_MESSAGES.NO_PRODUCT_AVAILABLE_TO_PURCHASE')}
						</Alert>
					) : (
							<Datagrid
								key="product_id"
								data={availableToPurchaseList}
								columns={baseColumns}
							rowClick={false}
							pageSize={5}
							showSearchBar={true}
							checkboxSelection={true}
							customButtons={customButtonsPurch}
								onSelect={handleRowSelectPurch}
								rowIdField="product_id"
								sortBy={[{ field: "purchase_name", sort: "asc" }]}
								hideFilterFromColumnMenu
								lockActiveColumnInManageColumns
								preventHidingLastColumn
							/>
					)}
				</>
			)}
			{activeTab === 2 && (
				<>
					<RequestsHistory />
				</>
			)}
		</div>
	);
};

export default Requests;
